import { useEffect, useMemo, useRef, useState } from 'react'
import { ResponsiveContainer, BarChart, CartesianGrid, XAxis, YAxis, Bar, Tooltip, Legend } from 'recharts'
import { getChartPalette } from '../../chartPalette'

const CHART_COLORS = getChartPalette()

const CustomLegend = ({ payload }) => {
  if (!payload || payload.length === 0) return null
  return (
    <div className="custom-legend-container">
      {payload.map((entry) => (
        <div className="legend-item" key={entry.value}>
          <span className="legend-color-dot" style={{ background: entry.color }} />
          <span className="legend-label">{entry.value}</span>
        </div>
      ))}
    </div>
  )
}
const toYMD = (d) => {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

const toYM = (d) => {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  return `${y}-${m}`
}

const parseYMD = (s) => {
  const [y, m, d] = s.split('-').map(Number)
  return new Date(y, m - 1, d || 1)
}

const addDays = (d, n) => {
  const x = new Date(d)
  x.setDate(x.getDate() + n)
  return x
}

const addMonths = (d, n) => {
  const x = new Date(d)
  x.setMonth(x.getMonth() + n)
  return x
}

const startOfMonth = (d) => {
  return new Date(d.getFullYear(), d.getMonth(), 1)
}

const endOfMonth = (d) => {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0)
}

const startOfYear = (d) => {
  return new Date(d.getFullYear(), 0, 1)
}

const endOfYear = (d) => {
  return new Date(d.getFullYear(), 11, 1)
}

const fillMissing = (data, isMonthly) => {
  if (!data?.length) return []

  const first = data[0].date
  const last = data[data.length - 1].date
  const rawStart = parseYMD(first)
  const rawEnd = parseYMD(isMonthly ? `${last}-01` : last)
  const start = isMonthly ? startOfYear(rawStart) : new Date(rawStart.getFullYear(), rawStart.getMonth(), 1)
  const end = isMonthly ? endOfYear(rawEnd) : endOfMonth(rawEnd)
  const has = new Map(data.map((r) => [r.date, r]))
  const out = []

  if (isMonthly) {
    for (let d = new Date(start.getFullYear(), start.getMonth(), 1); d <= end; d = addMonths(d, 1)) {
      const key = toYM(d)
      out.push(has.get(key) || { date: key, unique_sn: 0, total_faults: 0 })
    }
  } else {
    for (let d = new Date(start); d <= end; d = addDays(d, 1)) {
      const key = toYMD(d)
      out.push(has.get(key) || { date: key, unique_sn: 0, total_faults: 0 })
    }
  }

  return out
}

const buildTicks = (filledData, originalData, isMonthly) => {
  if (!filledData.length) return []

  const origKeys = new Set(originalData.map((r) => r.date))
  const start = parseYMD(filledData[0].date.length === 7 ? `${filledData[0].date}-01` : filledData[0].date)
  const end = parseYMD(
    filledData[filledData.length - 1].date.length === 7 ? `${filledData[filledData.length - 1].date}-01` : filledData[filledData.length - 1].date,
  )
  const tickSet = new Set()

  if (isMonthly) {
    for (let d = startOfYear(start); d <= endOfYear(end); d = addMonths(d, 1)) {
      tickSet.add(toYM(d))
    }
    origKeys.forEach((k) => tickSet.add(k))
  } else {
    for (let d = startOfMonth(start); d <= endOfMonth(end); d = addDays(d, 7)) {
      tickSet.add(toYMD(d))
    }
    origKeys.forEach((k) => tickSet.add(k))
  }

  const idx = new Map(filledData.map((r, i) => [r.date, i]))
  const ticks = Array.from(tickSet).filter((k) => idx.has(k))
  ticks.sort((a, b) => idx.get(a) - idx.get(b))

  return ticks
}

const Graph = ({ data = [] }) => {
  const containerRef = useRef(null)
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 })

  const isMonthly = useMemo(() => (data?.[0]?.date || '').length === 7, [data])

  const normalized = useMemo(
    () =>
      (Array.isArray(data) ? data : []).map((r) => ({
        ...r,
        unique_sn: r.unique_sn ?? 0,
        total_faults: r.total_faults ?? 0,
      })),
    [data],
  )
  
  const chartData = useMemo(() => fillMissing(normalized, isMonthly), [normalized, isMonthly])
  
  const xTicks = useMemo(() => buildTicks(chartData, Array.isArray(data) ? data : [], isMonthly), [chartData, data, isMonthly])

  const colorMap = useMemo(
    () => ({
      unique_sn: CHART_COLORS[7],
      total_faults: CHART_COLORS[10],
    }),
    [],
  )

  const formatTick = (label) => {
    if (!label) return ''
    if (isMonthly) {
      const [y, m] = label.split('-')
      return `${m}-${y}`
    }
    const [y, m, d] = label.split('-')
    return `${d}-${m}`
  }

  useEffect(() => {
    const observer = new window.ResizeObserver((entries) => {
      if (entries[0]) {
        const { width, height } = entries[0].contentRect
        setDimensions({ width, height })
      }
    })
    if (containerRef.current) observer.observe(containerRef.current)
    return () => observer.disconnect()
  }, [])

  return (
    <div ref={containerRef} style={{ width: '100%', height: 450 }} className="chart-responsive-wrapper">
      <div className="chart-min-width">
        {dimensions.width > 0 && dimensions.height > 0 && (
          <ResponsiveContainer width="100%" height="100%" key={`${dimensions.width}x${dimensions.height}`}>
            <BarChart
              data={chartData}
              margin={{ top: 20, right: 30, left: 0, bottom: 0 }}
              barCategoryGap={dimensions.width < 480 ? '20%' : '10%'}
              barGap={dimensions.width < 480 ? 0 : 2}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" ticks={xTicks} tickFormatter={formatTick} minTickGap={12} />
              <YAxis allowDecimals={false} />
              <Tooltip
                labelFormatter={(label) => {
                  if (!label) return ''
                  if (isMonthly) {
                    const [y, m] = label.split('-')
                    return `${m}-${y}`
                  }
                  const [y, m, d] = label.split('-')
                  return `${d}-${m}-${y}`
                }}
                formatter={(value, key) => {
                  const name = key === 'unique_sn' ? 'Unique SN CF' : key === 'total_faults' ? 'Total CF' : key
                  return [value, name]
                }}
                cursor={{ fill: 'rgba(0,0,0,0.06)' }}
              />
              <Legend content={CustomLegend} />
              <Bar dataKey="unique_sn" name="Unique SN CF" fill={colorMap.unique_sn} barSize={dimensions.width < 480 ? 8 : 12} />
              <Bar dataKey="total_faults" name="Total CF" fill={colorMap.total_faults} barSize={dimensions.width < 480 ? 8 : 12} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  )
}
export default Graph

